// hooks/gsc/useGSCTopPerformersMock.js
import { useMemo, useState } from 'react';
import { getMockGSCData } from '../../mock_data/mock_data/mockgscdata';

/**
 * Mock Version of GSC Top Performers Hook
 * Uses local mock data instead of API calls - perfect for demos!
 * 
 * @param {string} dimensionType - 'query' or 'page'
 * @param {object} dateRange - Date range object (not used in mock, but kept for API compatibility)
 * @param {number} limit - Number of top items to return (default 10)
 */
const useGSCTopPerformers = (
  dimensionType = 'query',
  dateRange = null,
  limit = 10
) => {
  const [isRefetching, setIsRefetching] = useState(false);
  const [dataUpdatedAt, setDataUpdatedAt] = useState(Date.now());

  // Simulate data fetching with mock data
  const apiData = useMemo(() => {
    return getMockGSCData(dimensionType);
  }, [dimensionType]);

  // Mock refetch function that simulates a refresh
  const refetch = async () => {
    setIsRefetching(true);
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setDataUpdatedAt(Date.now());
    setIsRefetching(false);
    
    return { data: apiData };
  };

  // Transform data for horizontal bar chart (same logic as real hook)
  const transformedData = useMemo(() => {
    if (!apiData) {
      return {
        chartData: [],
        topPerformer: null,
        totalClicks: 0,
        metadata: null,
        period: null,
      };
    }

    // API returns 'queries' or 'pages' key depending on dimension type
    const items = dimensionType === 'page' ? apiData.pages : apiData.queries;
    const { period, metadata } = apiData;

    // Helper to truncate long URLs for display
    const truncateURL = (url, maxLength = 50) => {
      if (url.length <= maxLength) return url;
      
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname + urlObj.search;
        if (path.length > maxLength - 15) {
          return urlObj.hostname + path.substring(0, maxLength - 18) + '...';
        }
        return urlObj.hostname + path;
      } catch {
        return url.substring(0, maxLength) + '...';
      }
    };

    // Chart data format for horizontal bars
    const chartData = items.map((item) => {
      const value = dimensionType === 'page' ? item.page : item.query;
      
      return {
        name: dimensionType === 'page' ? truncateURL(value) : value,
        fullValue: value, // Store full URL/query for tooltip
        clicks: item.clicks,
        impressions: item.impressions,
        ctr: item.ctr,
        position: item.position,
        percentage: item.percentage_of_clicks,
        rank: item.rank,
      };
    });

    // Top performer
    const topPerformer = items.length > 0 ? {
      value: dimensionType === 'page' ? items[0].page : items[0].query,
      displayValue: dimensionType === 'page' 
        ? truncateURL(items[0].page) 
        : items[0].query,
      clicks: items[0].clicks,
      percentage: items[0].percentage_of_clicks,
    } : null;

    return {
      chartData,
      topPerformer,
      totalClicks: metadata?.total_clicks || 0,
      totalImpressions: metadata?.total_impressions || 0,
      avgCTR: metadata?.avg_ctr || 0,
      metadata,
      period,
      dimensionType,
    };
  }, [apiData, dimensionType]);

  return {
    ...transformedData,
    loading: false, // Mock never loads
    refreshing: isRefetching,
    error: null, // Mock never errors
    isError: false,
    initialized: true, // Mock is always initialized
    refetch,
    lastUpdated: dataUpdatedAt,
  };
};

export default useGSCTopPerformers;